import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ShieldCheck,
  Radar,
  GitBranch,
  Sparkles,
  Lock,
  ScrollText,
  FlaskConical,
  Wallet,
  PhoneOff,
} from 'lucide-react';
import { MarketingNav, MarketingFooter } from '../../components/MarketingChrome';
import { Reveal } from '../../components/Reveal';
import { RuleChip } from '../../components/RuleChip';
import refineryNight from '../../assets/landing/refinery-night.jpg';
import controlRoom from '../../assets/landing/control-room.jpg';

const CYCLE_STATES = [
  { label: 'Clear', cls: 'clear' },
  { label: 'Advisory', cls: 'advisory' },
  { label: 'Critical — Stop Work', cls: 'critical' },
] as const;

const MARQUEE_ITEMS = [
  { rule: 'HOT-WORK-RADIUS-01', text: 'Hot work 6.2 m from an open confined-space entry — inside the 10.7 m spark radius.' },
  { rule: 'LOTO-DBB-03', text: 'Line-breaking permit lacks a verified double-block-and-bleed isolation on the upstream valve.' },
  { rule: 'SIMOPS-SHARED-AREA-07', text: 'Crane critical lift boom radius overlaps an active radiography exclusion zone.' },
  { rule: 'GAS-TEST-EXPIRY-02', text: 'Confined-space gas test expired 14 minutes ago — entry permit auto-flagged for stop-work.' },
  { rule: 'ELEC-ENERGY-ISO-05', text: 'Electrical isolation permit references a breaker that is not racked out on any verified certificate.' },
  { rule: 'FIREWATCH-DURATION-04', text: 'Hot work permit closed without the mandatory 30-minute post-work fire watch.' },
];

export default function LandingPage() {
  const [cycleIndex, setCycleIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setCycleIndex((i) => (i + 1) % CYCLE_STATES.length), 2600);
    return () => clearInterval(id);
  }, []);

  return (
    <div>
      <MarketingNav />
      <Hero cycleIndex={cycleIndex} />
      <PhoneCallMarquee />
      <BentoSection />
      <HowItWorksTeaser />
      <TrustSection />
      <FinalCta />
      <MarketingFooter />
    </div>
  );
}

function Hero({ cycleIndex }: { cycleIndex: number }) {
  const state = CYCLE_STATES[cycleIndex]!;
  return (
    <section className="hero">
      <div className="container hero-grid">
        <Reveal>
          <div className="hero-eyebrow"><ShieldCheck size={14} /> Sensifai Industrial AI Products</div>
          <h1 className="h1-hero">
            The permit conflict that would have caused an incident,<br />caught before anyone signs it.
          </h1>
          <p className="body-text" style={{ maxWidth: 520, marginTop: 20, fontSize: '1.0625rem' }}>
            PermitGuard AI is a deterministic Safe Work Permit and LOTO conflict engine. It cross-checks
            every hot work, confined space, line-breaking, and isolation permit against your live plant
            topology, in real time — and it fails closed, never open.
          </p>
          <div className="hero-cta-row">
            <Link to="/login" className="btn btn-primary">Try the live demo <ArrowRight size={16} /></Link>
            <Link to="/how-it-works" className="btn btn-secondary">See how it works</Link>
          </div>
          <div className="hero-proof-row">
            <div><div className="stat-num">10.7 m</div><div className="stat-label">Cited hot-work spark radius (NFPA 51B)</div></div>
            <div><div className="stat-num">100%</div><div className="stat-label">Deterministic — never AI-decided</div></div>
            <div><div className="stat-num">&lt;300ms</div><div className="stat-label">Typical full-plant evaluation</div></div>
          </div>
        </Reveal>
        <Reveal delay={1}>
          <PlantGraphVisual state={state} />
        </Reveal>
      </div>
    </section>
  );
}

function PlantGraphVisual({ state }: { state: { label: string; cls: string } }) {
  return (
    <div className="plant-graph-card">
      <div className="plant-graph-badge-wrap">
        <span key={state.label} className={`status-badge ${state.cls} cycling-badge-fade`}>{state.label}</span>
      </div>
      <svg viewBox="0 0 420 320" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path className="plant-graph-edge" d="M60 60 L160 60 L160 140" />
        <path className="plant-graph-edge" d="M160 140 L260 140 L260 60" />
        <path className="plant-graph-edge" d="M160 140 L160 230 L260 230" />
        <path className="plant-graph-edge" d="M260 230 L340 230 L340 150" />
        <path className="plant-graph-edge" d="M260 60 L340 60 L340 150" />
        <rect x="30" y="36" width="60" height="48" rx="8" className="plant-graph-node" />
        <rect x="130" y="116" width="60" height="48" rx="8" className={`plant-graph-node ${state.cls === 'critical' ? 'flagged' : ''}`} />
        <circle cx="260" cy="60" r="26" className="plant-graph-node" />
        <rect x="230" y="206" width="60" height="48" rx="8" className="plant-graph-node" />
        <circle cx="340" cy="150" r="28" className={`plant-graph-node ${state.cls === 'advisory' ? 'flagged' : ''}`} />
        <text x="40" y="112" fill="#8fb4e6" fontSize="11" fontFamily="IBM Plex Mono, monospace">V-102</text>
        <text x="128" y="184" fill="#8fb4e6" fontSize="11" fontFamily="IBM Plex Mono, monospace">L-204</text>
        <text x="238" y="46" fill="#8fb4e6" fontSize="11" fontFamily="IBM Plex Mono, monospace">TK-08</text>
        <text x="234" y="272" fill="#8fb4e6" fontSize="11" fontFamily="IBM Plex Mono, monospace">P-301</text>
        <text x="318" y="196" fill="#8fb4e6" fontSize="11" fontFamily="IBM Plex Mono, monospace">BRK-14</text>
      </svg>
    </div>
  );
}

function PhoneCallMarquee() {
  const doubled = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS];
  return (
    <section className="marquee-section">
      <div className="container" style={{ marginBottom: 32 }}>
        <Reveal>
          <div className="row gap-2" style={{ color: 'var(--text-on-dark)' }}>
            <PhoneOff size={20} color="#8fb4e6" />
            <span className="eyebrow" style={{ color: '#8fb4e6' }}>The 11pm phone call</span>
          </div>
          <h2 className="h2-section" style={{ color: '#fff', marginTop: 8, maxWidth: 640 }}>
            Every one of these is a real rule the engine enforces — not marketing copy.
          </h2>
        </Reveal>
      </div>
      <div className="marquee-track">
        {doubled.map((item, i) => (
          <div className="marquee-chip" key={i}>
            <RuleChip code={item.rule} />
            <span>{item.text}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function BentoSection() {
  return (
    <section className="section container">
      <Reveal>
        <div className="eyebrow">The engine</div>
        <h2 className="h2-section" style={{ marginTop: 8, maxWidth: 640 }}>One deterministic core. Four kinds of hazard it never misses.</h2>
      </Reveal>
      <div className="bento-grid" style={{ marginTop: 40 }}>
        <BentoCell className="wide">
          <div className="bento-icon"><Radar size={22} /></div>
          <h3 className="h3">Spatial conflict detection (PostGIS)</h3>
          <p className="body-small text-secondary" style={{ maxWidth: 560 }}>
            Hot work, radiography exclusion zones, and crane boom radii are checked against real plant
            coordinates and elevation — not a checklist. If two permits' geometry overlaps within a
            cited safety radius, it's flagged before either is issued.
          </p>
        </BentoCell>
        <BentoCell>
          <div className="bento-icon"><GitBranch size={22} /></div>
          <h3 className="h3">Topological isolation graph</h3>
          <p className="body-small text-secondary">Walks the piping and electrical graph to verify true double-block-and-bleed and positive isolation — not just "a valve is closed somewhere".</p>
        </BentoCell>
        <BentoCell>
          <div className="bento-icon"><Lock size={22} /></div>
          <h3 className="h3">De-isolation exclusivity</h3>
          <p className="body-small text-secondary">A shared lockbox can't be released while any other active permit still depends on it — enforced by the same rule at apply-time and release-time.</p>
        </BentoCell>
        <BentoCell className="tall">
          <div className="bento-icon"><Sparkles size={22} /></div>
          <h3 className="h3">AI where it actually helps</h3>
          <p className="body-small text-secondary">
            An LLM parses free-text scope descriptions into candidate equipment tags and writes
            plain-language explanations of conflicts. It never resolves a conflict, never approves a
            permit, and every output carries a visible AI-disclosure badge.
          </p>
        </BentoCell>
        <BentoCell>
          <div className="bento-icon"><FlaskConical size={22} /></div>
          <h3 className="h3">Gas test &amp; fire-watch timers</h3>
          <p className="body-small text-secondary">Confined-space and hot-work permits track live expiry windows, not paper timestamps.</p>
        </BentoCell>
        <BentoCell>
          <div className="bento-icon"><ScrollText size={22} /></div>
          <h3 className="h3">Tamper-evident audit chain</h3>
          <p className="body-small text-secondary">Every decision is SHA-256 hash-chained. Regulators and insurers can verify it was never altered after the fact.</p>
        </BentoCell>
      </div>
    </section>
  );
}

function BentoCell({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  function onMove(e: MouseEvent<HTMLDivElement>) {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${e.clientX - rect.left}px`);
    el.style.setProperty('--my', `${e.clientY - rect.top}px`);
  }
  return (
    <div ref={ref} className={`bento-cell ${className}`} onMouseMove={onMove}>
      {children}
    </div>
  );
}

function HowItWorksTeaser() {
  return (
    <section className="section" style={{ background: 'var(--surface-alt)' }}>
      <div className="container">
        <div className="grid-2" style={{ alignItems: 'center', gap: 56 }}>
          <Reveal>
            <img
              src={controlRoom}
              alt="Safety officers reviewing permit dashboards in a plant control room"
              style={{ width: '100%', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-elevated)' }}
            />
          </Reveal>
          <Reveal delay={1}>
            <div className="eyebrow">From scope text to sign-off</div>
            <h2 className="h2-section" style={{ marginTop: 8 }}>Four steps, one authoritative answer.</h2>
            <p className="body-text" style={{ marginTop: 12 }}>
              A requester drafts a permit, the engine evaluates it against every active permit and
              isolation certificate on the plant, conflicts surface with plain-language explanations,
              and a human — never the AI — decides whether an override is justified.
            </p>
            <Link to="/how-it-works" className="btn btn-secondary" style={{ marginTop: 20, width: 'fit-content' }}>
              Walk through the full workflow <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function TrustSection() {
  return (
    <section className="section" style={{ position: 'relative', color: '#fff', overflow: 'hidden' }}>
      <img src={refineryNight} alt="" aria-hidden style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(10,37,64,0.88), rgba(10,37,64,0.94))' }} />
      <div className="container" style={{ position: 'relative' }}>
        <Reveal>
          <div className="eyebrow" style={{ color: '#8fb4e6' }}>Built for the plants where this matters most</div>
          <h2 className="h2-section" style={{ color: '#fff', maxWidth: 680, marginTop: 8 }}>
            Refineries, petrochemical complexes, and offshore platforms run permits that, if they
            collide, don't just cost money — they cost lives.
          </h2>
          <div className="grid-3" style={{ marginTop: 40 }}>
            <TrustStat icon={<ShieldCheck size={20} />} label="Fail-closed by design" text="Any engine or AI error blocks the permit as SYSTEM_INDETERMINATE_BLOCK — never a silent pass." />
            <TrustStat icon={<Wallet size={20} />} label="Usage-metered, marketplace-billed" text="Every evaluation and AI extraction reports as a real usage event through Sensifai Marketplace." />
            <TrustStat icon={<Lock size={20} />} label="Dual sign-off overrides" text="A critical stop-work finding can only proceed with an independent second authorization." />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function TrustStat({ icon, label, text }: { icon: ReactNode; label: string; text: string }) {
  return (
    <div className="stack gap-2" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 'var(--radius-lg)', padding: 24 }}>
      <div style={{ color: '#8fb4e6' }}>{icon}</div>
      <div style={{ fontWeight: 700 }}>{label}</div>
      <p className="body-small" style={{ color: '#c3d2e8' }}>{text}</p>
    </div>
  );
}

function FinalCta() {
  return (
    <section className="section container" style={{ textAlign: 'center' }}>
      <Reveal>
        <h2 className="h2-section">Ready to see a conflict get caught in real time?</h2>
        <p className="body-text" style={{ maxWidth: 480, margin: '12px auto 0' }}>
          No sales call required — the live demo runs against a seeded refinery with real permits,
          real isolation certificates, and real conflicts waiting to be found.
        </p>
        <div className="hero-cta-row" style={{ justifyContent: 'center' }}>
          <Link to="/login" className="btn btn-primary">Launch the demo <ArrowRight size={16} /></Link>
        </div>
        <p className="easter-hint" style={{ marginTop: 28 }}>psst — hover any rule code on this page. They're all real.</p>
      </Reveal>
    </section>
  );
}
